import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GatewayRetryDialog } from '@/components/recharge/GatewayRetryDialog';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { RechargePlatformPicker } from '@/components/recharge/RechargePlatformPicker';
import { RechargePlanGrid } from '@/components/recharge/RechargePlanGrid';
import { RechargeCheckoutCard } from '@/components/recharge/RechargeCheckoutCard';
import { CustomRechargeCard } from '@/components/recharge/CustomRechargeCard';
import { NobleRechargeCard } from '@/components/recharge/NobleRechargeCard';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { LimitContext } from '@/components/recharge/LimitWarning';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { loadRazorpayScript, openRazorpayCheckout, UPI_ONLY_CONFIG } from '@/lib/razorpay';
import { PLATFORM_ACCENTS } from '@/lib/format';
import { Sparkles, Diamond, Crown, MapPin, IndianRupee, LayoutGrid } from 'lucide-react';

const INITIAL_POLICY = { refund: false, privacy: false, fairuse: false };
const VERIFIABLE_PLATFORMS = ['trumeet', 'olaparty'];

export default function RechargePage() {
    const navigate = useNavigate();
    const { user, setUser } = useAuth();
    const [limits, setLimits] = useState({ exempt: false, used_today: 0, remaining: 15000 });
    const loadLimits = useCallback(() => api.get('/recharge/limits').then(r => setLimits(r.data)).catch(() => {}), []);
    useEffect(() => { loadLimits(); }, [loadLimits]);
    const maxLimit = (limits.exempt || user?.limit_exempt) ? Infinity : limits.remaining;
    const [billingOpen, setBillingOpen] = useState(false);
    const [billForm, setBillForm] = useState({ city: '', state: '', pincode: '' });
    const [savingBill, setSavingBill] = useState(false);
    const billingComplete = Boolean((user?.city || '').trim() && (user?.state || '').trim());

    useEffect(() => {
        if (user && !billingComplete) {
            setBillForm({ city: user.city || '', state: user.state || '', pincode: user.pincode || '' });
            setBillingOpen(true);
        } else {
            setBillingOpen(false);
        }
    }, [user, billingComplete]);

    const submitBilling = async () => {
        if (!billForm.city.trim() || !billForm.state.trim()) { toast.error('Please enter both city and state'); return; }
        setSavingBill(true);
        try {
            const { data } = await api.patch('/auth/profile', billForm);
            setUser(data.user);
            toast.success('Billing address saved — you can recharge now');
            setBillingOpen(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not save billing address');
        } finally { setSavingBill(false); }
    };

    const [params, setParams] = useSearchParams();
    const [platforms, setPlatforms] = useState([]);
    const [platformKey, setPlatformKey] = useState(params.get('platform') || 'olaparty');
    const [plans, setPlans] = useState([]);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [inAppId, setInAppId] = useState('');
    const [recipientInfo, setRecipientInfo] = useState(null);
    const [verifying, setVerifying] = useState(false);
    const [verifyError, setVerifyError] = useState('');
    const [gateways, setGateways] = useState([{ key: 'razorpay', name: 'Razorpay', implemented: true }]);
    const [gateway, setGateway] = useState('razorpay');
    const [policy, setPolicy] = useState(INITIAL_POLICY);
    const [loading, setLoading] = useState(false);
    const [retryOffer, setRetryOffer] = useState(null); // {from, to, reason, kind}
    const declineRef = useRef(null);
    const lastKindRef = useRef('plan');
    const retryTo = params.get('retry');

    // Noble (Qwick Live only)
    const [opMode, setOpMode] = useState('diamonds'); // 'diamonds' | 'noble'
    const [nobleTiers, setNobleTiers] = useState([]);
    const [nobleDiscount, setNobleDiscount] = useState(0);
    const [nobleTier, setNobleTier] = useState('');
    const [nobleMode, setNobleMode] = useState('first');
    const [currentNoble, setCurrentNoble] = useState(null); // detected current tier for the recipient

    // Custom amount (INR -> diamonds)
    const [payMode, setPayMode] = useState('amount'); // 'amount' | 'plan'
    const [amount, setAmount] = useState('');
    const [rateInfo, setRateInfo] = useState({ rate: 1.3, multiplier: 1, defaultRate: 1.3 });

    useEffect(() => {
        api.get('/platforms').then(r => {
            const allowed = user?.allowed_platforms;
            const list = (r.data.platforms || []).filter(p => !allowed || allowed.includes(p.key));
            setPlatforms(list);
            if (list.length && !list.some(p => p.key === platformKey)) setPlatformKey(list[0].key);
        });
    }, [user?.allowed_platforms]); // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch effective INR->diamond rate + multiplier for the current platform.
    useEffect(() => {
        if (!platformKey) return;
        api.get('/recharge/rate', { params: { platform: platformKey } })
            .then(r => setRateInfo({ rate: r.data.rate, multiplier: r.data.multiplier, defaultRate: r.data.default_rate ?? r.data.rate }))
            .catch(() => setRateInfo({ rate: 1.3, multiplier: platformKey === 'olaparty' ? 200 : 1, defaultRate: 1.3 }));
    }, [platformKey]);

    // Load noble tiers (only relevant for Qwick Live).
    useEffect(() => {
        api.get('/recharge/noble/tiers').then(r => {
            const t = r.data.tiers || [];
            setNobleTiers(t);
            setNobleDiscount(r.data.discount_pct || 0);
            if (t.length) {
                setNobleTier(t[0].key);
                setNobleMode(Number(t[0].price?.first || 0) > 0 ? 'first' : 'renew');
            }
        }).catch(() => setNobleTiers([]));
    }, []);

    const nobleAvailable = platformKey === 'olaparty' && nobleTiers.length > 0;
    useEffect(() => {
        if (!nobleAvailable && opMode === 'noble') setOpMode('diamonds');
    }, [nobleAvailable, opMode]);

    useEffect(() => {
        if (!platformKey) return;
        api.get(`/platforms/${platformKey}/plans`).then(r => {
            setPlans(r.data.plans || []);
            const planId = params.get('plan');
            const found = planId ? (r.data.plans || []).find(p => p.id === planId) : null;
            setSelectedPlan(found || null);
        });
    }, [platformKey, params]);

    useEffect(() => {
        api.get('/recharge/gateways').then(r => {
            const gws = r.data.gateways || [];
            if (gws.length) {
                setGateways(gws);
                const impl = gws.find(g => g.implemented && g.key === retryTo) || gws.find(g => g.implemented) || gws[0];
                setGateway(impl.key);
            }
        }).catch(() => { });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Coming back from a failed Pine Labs checkout: restore the form and offer a one-tap retry.
    useEffect(() => {
        if (!retryTo) return;
        let snap = null;
        try { snap = JSON.parse(sessionStorage.getItem('ee_retry') || 'null'); } catch (_) { snap = null; }
        if (!snap) return;
        sessionStorage.removeItem('ee_retry');
        setInAppId(snap.in_app_id || '');
        setRecipientInfo(snap.recipient || null);
        if (snap.policy) setPolicy(snap.policy);
        if (snap.op_mode) setOpMode(snap.op_mode);
        if (snap.pay_mode) setPayMode(snap.pay_mode);
        if (snap.amount) setAmount(String(snap.amount));
        if (snap.noble_tier) setNobleTier(snap.noble_tier);
        if (snap.noble_mode) setNobleMode(snap.noble_mode);
        setRetryOffer({ from: snap.gateway || 'pinelabs', to: retryTo, reason: snap.reason || '', kind: snap.kind });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const platformMeta = useMemo(() => platforms.find(p => p.key === platformKey), [platforms, platformKey]);
    const accent = PLATFORM_ACCENTS[platformKey] || PLATFORM_ACCENTS.olaparty;
    const allPoliciesAccepted = policy.refund && policy.privacy && policy.fairuse;
    const verifiable = VERIFIABLE_PLATFORMS.includes(platformKey);

    const handleInAppIdChange = useCallback((v) => {
        setInAppId(v);
        setRecipientInfo(null);
        setVerifyError('');
    }, []);

    const verifyRecipient = useCallback(async () => {
        const id = inAppId.trim();
        if (!verifiable || !id || recipientInfo || verifying) return;
        setVerifying(true);
        setVerifyError('');
        try {
            const { data } = await api.get(`/recharge/lookup/${encodeURIComponent(id)}`, { params: { platform: platformKey } });
            setRecipientInfo(data);
        } catch (e) {
            setRecipientInfo(null);
            setVerifyError(e.response?.data?.detail || 'Could not verify this ID. Please check and try again.');
        } finally {
            setVerifying(false);
        }
    }, [inAppId, verifiable, recipientInfo, verifying, platformKey]);

    // Restored retry snapshot without a verified recipient: verify automatically.
    useEffect(() => {
        if (retryOffer && verifiable && inAppId.trim() && !recipientInfo && !verifying && !verifyError) verifyRecipient();
    }, [retryOffer, verifiable, inAppId, recipientInfo, verifying, verifyError, verifyRecipient]);

    const handlePolicyChange = useCallback((key, value) => {
        setPolicy(prev => ({ ...prev, [key]: value }));
    }, []);

    // Auto-detect the recipient's CURRENT nobility once verified (Qwick Live noble flow).
    useEffect(() => {
        if (opMode !== 'noble' || !recipientInfo || !inAppId.trim()) { setCurrentNoble(null); return; }
        let cancelled = false;
        api.get(`/recharge/noble/detect/${encodeURIComponent(inAppId.trim())}`).then(({ data }) => {
            if (cancelled || !data.ok) return;
            const cur = data.current || {};
            setCurrentNoble(cur);
            if (cur.has_noble && cur.tier_key) {
                setNobleTier(cur.tier_key);
                const t = nobleTiers.find(x => x.key === cur.tier_key);
                if (!t || Number(t.price?.renew || 0) > 0) setNobleMode('renew');
            }
        }).catch(() => { if (!cancelled) setCurrentNoble(null); });
        return () => { cancelled = true; };
    }, [opMode, recipientInfo, inAppId, nobleTiers]);

    const handlePlatformChange = useCallback((v) => {
        setPlatformKey(v);
        setSelectedPlan(null);
        setRecipientInfo(null);
        setVerifyError('');
        setOpMode('diamonds');
        setParams({ platform: v });
    }, [setParams]);

    const handleVerifyResult = useCallback((resp) => {
        loadLimits();
        const status = resp.payment_status;
        if (status === 'completed') toast.success('Recharge completed! 🎉');
        else if (status === 'pending') toast.info('Payment received. Recharge is being processed.');
        else toast.warning('Payment received but bridge had an issue. Admin notified.');
        setLoading(false);
        setSelectedPlan(null);
        setInAppId('');
        setAmount('');
        setRecipientInfo(null);
        setVerifyError('');
        setPolicy(INITIAL_POLICY);
    }, [loadLimits]);

    // Shared checkout: Pine Labs (hosted UPI redirect), mock, or Razorpay (UPI-only).
    const payWithGateway = async (data, { description, notes, theme = { color: '#0E2A47' } }) => {
        declineRef.current = null;
        if (data.gateway === 'pinelabs' && data.redirect_url) {
            try {
                sessionStorage.setItem('ee_retry', JSON.stringify({
                    gateway: 'pinelabs', kind: lastKindRef.current, platform: platformKey, plan_id: selectedPlan?.id,
                    in_app_id: inAppId.trim(), recipient: recipientInfo, policy, op_mode: opMode, pay_mode: payMode,
                    amount, noble_tier: nobleTier, noble_mode: nobleMode,
                }));
            } catch (_) { /* ignore */ }
            toast.message('Redirecting to Pine Labs secure UPI checkout…');
            window.location.assign(data.redirect_url);
            return;
        }
        if (data.razorpay?.mock) {
            toast.message('Razorpay keys not configured. Using mock checkout for testing.');
            const verifyRes = await api.post('/recharge/verify', {
                payment_id: data.payment_id,
                razorpay_order_id: data.razorpay.order_id,
                razorpay_payment_id: `pay_mock_${Date.now()}`,
                razorpay_signature: `mock_${Date.now()}`,
            });
            handleVerifyResult(verifyRes.data);
            return;
        }
        const loaded = await loadRazorpayScript();
        if (!loaded) { toast.error('Razorpay failed to load. Check internet.'); setLoading(false); return; }
        const options = {
            key: data.razorpay.key_id,
            amount: data.razorpay.amount,
            currency: data.razorpay.currency,
            order_id: data.razorpay.order_id,
            name: 'Eagle Eyes Services',
            description,
            prefill: { email: user.email, name: user.name || '', contact: user.mobile || user.whatsapp || '' },
            notes,
            config: UPI_ONLY_CONFIG,
            theme,
            handler: async (rsp) => {
                declineRef.current = null;
                try {
                    const verifyRes = await api.post('/recharge/verify', {
                        payment_id: data.payment_id,
                        razorpay_order_id: rsp.razorpay_order_id,
                        razorpay_payment_id: rsp.razorpay_payment_id,
                        razorpay_signature: rsp.razorpay_signature,
                    });
                    handleVerifyResult(verifyRes.data);
                } catch (e) {
                    toast.error('Verification failed. Please contact support with your payment ID.');
                }
            },
            modal: {
                ondismiss: () => {
                    setLoading(false);
                    const d = declineRef.current;
                    declineRef.current = null;
                    const alt = d && gateways.find(g => g.key !== 'razorpay' && g.implemented);
                    if (alt) setRetryOffer({ from: 'razorpay', to: alt.key, reason: d.reason, kind: lastKindRef.current });
                },
            },
        };
        openRazorpayCheckout(options, (err) => {
            api.post('/recharge/payment-failed', {
                payment_id: data.payment_id,
                code: err.code, description: err.description, reason: err.reason,
                source: err.source, step: err.step, gateway_payment_id: err.metadata?.payment_id,
            }).catch(() => {});
            const why = err.description || 'The payment was declined.';
            declineRef.current = { reason: why };
            const hint = (err.source === 'bank' || err.source === 'customer' || /limit|exceed/i.test(`${err.reason} ${why}`))
                ? ' Your bank/UPI app may have a per-transaction limit — try a smaller amount or another UPI app/bank account.'
                : '';
            toast.error(`Payment failed: ${why}${hint}`, { duration: 12000 });
        });
    };

    const handlePay = async (gwArg) => {
        const gw = typeof gwArg === 'string' ? gwArg : gateway;
        lastKindRef.current = 'plan';
        if (!user) { toast.error('Please login to continue'); return; }
        if (!(user.city || '').trim() || !(user.state || '').trim()) { toast.error('Please complete your billing address (city & state) in your dashboard first.'); navigate('/dashboard'); return; }
        if (!selectedPlan) { toast.error('Choose a plan'); return; }
        if (!inAppId.trim()) { toast.error('Enter your in-app ID'); return; }
        if (verifiable && !recipientInfo) { toast.error('Please verify your in-app ID first'); return; }
        if (!allPoliciesAccepted) { toast.error('Please accept all policies'); return; }
        setLoading(true);
        try {
            const { data } = await api.post('/recharge/initiate', {
                platform: platformKey,
                plan_id: selectedPlan.id,
                in_app_id: inAppId.trim(),
                policies_accepted: true,
                gateway: gw,
            });

            await payWithGateway(data, {
                description: `${platformMeta?.name} recharge - ${selectedPlan.label || ''}`.trim(),
                notes: { platform: platformKey, in_app_id: inAppId.trim(), city: user.city || '', state: user.state || '', pincode: user.pincode || '' },
            });
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to initiate payment');
            setLoading(false);
        }
    };

    const handlePayCustom = async (gwArg) => {
        const gw = typeof gwArg === 'string' ? gwArg : gateway;
        lastKindRef.current = 'custom';
        if (!user) { toast.error('Please login to continue'); return; }
        if (!(user.city || '').trim() || !(user.state || '').trim()) { toast.error('Please complete your billing address (city & state) in your dashboard first.'); navigate('/dashboard'); return; }
        const amt = parseFloat(amount) || 0;
        if (amt <= 0) { toast.error('Enter a valid amount'); return; }
        if (!inAppId.trim()) { toast.error('Enter your in-app ID'); return; }
        if (verifiable && !recipientInfo) { toast.error('Please verify your in-app ID first'); return; }
        if (!allPoliciesAccepted) { toast.error('Please accept all policies'); return; }
        setLoading(true);
        try {
            const { data } = await api.post('/recharge/custom/initiate', {
                platform: platformKey,
                amount_inr: amt,
                in_app_id: inAppId.trim(),
                policies_accepted: true,
                gateway: gw,
            });
            await payWithGateway(data, {
                description: `${platformMeta?.name} recharge - ₹${amt}`,
                notes: { platform: platformKey, kind: 'custom', in_app_id: inAppId.trim(), city: user.city || '', state: user.state || '', pincode: user.pincode || '' },
            });
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to initiate payment');
            setLoading(false);
        }
    };

    const selectedNoble = useMemo(() => nobleTiers.find(t => t.key === nobleTier), [nobleTiers, nobleTier]);
    const noblePrice = selectedNoble ? Number(selectedNoble.price?.[nobleMode] || 0) : 0;

    const handlePayNoble = async (gwArg) => {
        const gw = typeof gwArg === 'string' ? gwArg : gateway;
        lastKindRef.current = 'noble';
        if (!user) { toast.error('Please login to continue'); return; }
        if (!(user.city || '').trim() || !(user.state || '').trim()) { toast.error('Please complete your billing address (city & state) in your dashboard first.'); navigate('/dashboard'); return; }
        if (!selectedNoble || noblePrice <= 0) { toast.error('Choose a noble title'); return; }
        if (!inAppId.trim()) { toast.error('Enter your in-app ID'); return; }
        if (!recipientInfo) { toast.error('Please verify your in-app ID first'); return; }
        if (!allPoliciesAccepted) { toast.error('Please accept all policies'); return; }
        setLoading(true);
        try {
            const { data } = await api.post('/recharge/noble/initiate', {
                tier: nobleTier,
                mode: nobleMode,
                in_app_id: inAppId.trim(),
                policies_accepted: true,
                gateway: gw,
            });
            await payWithGateway(data, {
                description: `${data.tier.label} noble (${nobleMode === 'renew' ? 'Renewal' : 'First-time'})`,
                notes: { platform: 'olaparty', kind: 'noble', tier: nobleTier, in_app_id: inAppId.trim(), city: user.city || '', state: user.state || '', pincode: user.pincode || '' },
                theme: { color: '#F59E0B' },
            });
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to initiate payment');
            setLoading(false);
        }
    };

    return (
        <LimitContext.Provider value={{ used: limits.used_today, remaining: limits.remaining, resetsAt: limits.resets_at }}>
        <AppShell>
            <GatewayRetryDialog offer={retryOffer} gateways={gateways} onClose={() => setRetryOffer(null)}
                onRetry={(o) => { setRetryOffer(null); setGateway(o.to); ({ plan: handlePay, custom: handlePayCustom, noble: handlePayNoble }[o.kind] || handlePay)(o.to); }} />
            <div className="max-w-5xl mx-auto">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
                    <Sparkles className="h-3.5 w-3.5" /> Recharge
                </div>
                <h1 className="font-display text-2xl sm:text-3xl font-semibold mt-1">Top up diamonds & coins</h1>
                <p className="text-sm text-muted-foreground mt-1">Choose a platform, pick a plan, and pay securely by UPI via Razorpay or Pine Labs.</p>

                <RechargePlatformPicker
                    platforms={platforms}
                    value={platformKey}
                    onChange={handlePlatformChange}
                />

                {nobleAvailable && (
                            <div className="mt-6" data-testid="user-op-mode-selector">
                                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">What do you want to buy?</div>
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setOpMode('diamonds')}
                                        className={`flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl border text-sm font-medium transition-colors ${opMode === 'diamonds' ? 'bg-[#0E2A47] text-white border-[#0E2A47]' : 'bg-white text-foreground border-border hover:border-[#0E2A47]/40'}`}
                                        data-testid="user-op-mode-diamonds"
                                    >
                                        <Diamond className="h-4 w-4" /> Diamonds
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setOpMode('noble')}
                                        className={`flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl border text-sm font-medium transition-colors ${opMode === 'noble' ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-foreground border-border hover:border-amber-400'}`}
                                        data-testid="user-op-mode-noble"
                                    >
                                        <Crown className="h-4 w-4" /> Noble Title
                                    </button>
                                </div>
                            </div>
                        )}

                        {opMode === 'noble' && nobleAvailable ? (
                            <NobleRechargeCard
                                platformMeta={platformMeta}
                                tiers={nobleTiers}
                                discountPct={nobleDiscount}
                                tier={nobleTier}
                                onTierChange={(k) => {
                                    setNobleTier(k);
                                    const t = nobleTiers.find(x => x.key === k);
                                    if (t && Number(t.price?.[nobleMode] || 0) <= 0) {
                                        setNobleMode(Number(t.price?.first || 0) > 0 ? 'first' : 'renew');
                                    }
                                }}
                                mode={nobleMode}
                                onModeChange={setNobleMode}
                                currentNoble={currentNoble}
                                inAppId={inAppId}
                                onInAppIdChange={handleInAppIdChange}
                                onInAppIdBlur={verifyRecipient}
                                verifying={verifying}
                                recipientInfo={recipientInfo}
                                verifyError={verifyError}
                                policy={policy}
                                onPolicyChange={handlePolicyChange}
                                allPoliciesAccepted={allPoliciesAccepted}
                                gateways={gateways}
                                gateway={gateway}
                                onGatewayChange={setGateway}
                                loading={loading}
                                onPay={handlePayNoble}
                                maxLimit={maxLimit}
                            />
                        ) : (
                            <>
                                <div className="mt-6" data-testid="user-pay-mode-selector">
                                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">How would you like to recharge?</div>
                                    <div className="flex gap-2">
                                        <button type="button" onClick={() => setPayMode('amount')}
                                            className={`flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl border text-sm font-medium transition-colors ${payMode === 'amount' ? 'bg-[#0E2A47] text-white border-[#0E2A47]' : 'bg-white text-foreground border-border hover:border-[#0E2A47]/40'}`}
                                            data-testid="user-pay-mode-amount">
                                            <IndianRupee className="h-4 w-4" /> Enter amount
                                        </button>
                                        <button type="button" onClick={() => setPayMode('plan')}
                                            className={`flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl border text-sm font-medium transition-colors ${payMode === 'plan' ? 'bg-[#0E2A47] text-white border-[#0E2A47]' : 'bg-white text-foreground border-border hover:border-[#0E2A47]/40'}`}
                                            data-testid="user-pay-mode-plan">
                                            <LayoutGrid className="h-4 w-4" /> Choose a plan
                                        </button>
                                    </div>
                                </div>

                                {payMode === 'amount' ? (
                                    <CustomRechargeCard
                                        platformMeta={platformMeta}
                                        platformKey={platformKey}
                                        rate={rateInfo.rate}
                                        multiplier={rateInfo.multiplier}
                                        defaultRate={rateInfo.defaultRate}
                                        amount={amount}
                                        onAmountChange={setAmount}
                                        inAppId={inAppId}
                                        onInAppIdChange={handleInAppIdChange}
                                        onInAppIdBlur={verifyRecipient}
                                        verifiable={verifiable}
                                        verifying={verifying}
                                        recipientInfo={recipientInfo}
                                        verifyError={verifyError}
                                        policy={policy}
                                        onPolicyChange={handlePolicyChange}
                                        allPoliciesAccepted={allPoliciesAccepted}
                                        gateways={gateways}
                                        gateway={gateway}
                                        onGatewayChange={setGateway}
                                        loading={loading}
                                        onPay={handlePayCustom}
                                        maxLimit={maxLimit}
                                    />
                                ) : (
                                    <>
                                        <RechargePlanGrid
                                            plans={plans}
                                            platformKey={platformKey}
                                            unitLabel={platformMeta?.unit}
                                            selectedPlanId={selectedPlan?.id}
                                            onSelect={setSelectedPlan}
                                        />
                                        <RechargeCheckoutCard
                                            platformMeta={platformMeta}
                                            platformKey={platformKey}
                                            accent={accent}
                                            selectedPlan={selectedPlan}
                                            inAppId={inAppId}
                                            onInAppIdChange={handleInAppIdChange}
                                            policy={policy}
                                            onPolicyChange={handlePolicyChange}
                                            allPoliciesAccepted={allPoliciesAccepted}
                                            loading={loading}
                                            onPay={handlePay}
                                            maxLimit={maxLimit}
                                            verifiable={verifiable}
                                            verifying={verifying}
                                            recipientInfo={recipientInfo}
                                            verifyError={verifyError}
                                            onInAppIdBlur={verifyRecipient}
                                            gateways={gateways}
                                            gateway={gateway}
                                            onGatewayChange={setGateway}
                                        />
                                    </>
                                )}
                            </>
                        )}
            </div>

            <Dialog open={billingOpen} onOpenChange={(o) => { if (!o && billingComplete) setBillingOpen(false); }}>
                <DialogContent className="max-w-md" data-testid="recharge-billing-modal" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2"><MapPin className="h-4 w-4 text-[#1F9D8A]" /> Complete your billing address</DialogTitle>
                    </DialogHeader>
                    <DialogDescription className="text-sm text-muted-foreground">Please add your city and state to continue with recharges. This is saved to your billing address.</DialogDescription>
                    <div className="mt-4 space-y-3">
                        <div className="space-y-1.5">
                            <Label className="text-xs">City <span className="text-rose-500">*</span></Label>
                            <Input value={billForm.city} onChange={e => setBillForm(f => ({ ...f, city: e.target.value }))} placeholder="City" data-testid="recharge-billing-city" autoFocus />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs">State <span className="text-rose-500">*</span></Label>
                            <Input value={billForm.state} onChange={e => setBillForm(f => ({ ...f, state: e.target.value }))} placeholder="State" data-testid="recharge-billing-state" />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs">Pincode <span className="text-muted-foreground">(optional)</span></Label>
                            <Input value={billForm.pincode} onChange={e => setBillForm(f => ({ ...f, pincode: e.target.value }))} placeholder="Pincode" data-testid="recharge-billing-pincode" />
                        </div>
                    </div>
                    <div className="mt-4 flex justify-end gap-2">
                        <Button variant="outline" onClick={() => navigate('/dashboard')} data-testid="recharge-billing-cancel">Back to dashboard</Button>
                        <Button onClick={submitBilling} disabled={savingBill} className="bg-[#0E2A47] hover:bg-[#0B223A]" data-testid="recharge-billing-submit">
                            {savingBill ? 'Saving…' : 'Submit'}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </AppShell>
        </LimitContext.Provider>
    );
}
